import React, { useEffect, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { postRequest } from "@react/helpers/fetch"
import Modal  from "@react/shared/ui/Modal"
import Button from "@react/shared/ui/Button"

const MODE_VISUALIZE = "visualize_data"
const MODE_SEND      = "send_by_email"

const CsvExportModal = ({ isOpen, onClose, csvUrl, ids, count, defaultEmail }) => {
  const [mode,       setMode]       = useState(MODE_VISUALIZE)
  const [recipient,  setRecipient]  = useState(defaultEmail || "")
  const [withLines,  setWithLines]  = useState(false)
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    if (!isOpen) return
    setMode(MODE_VISUALIZE)
    setRecipient(defaultEmail || "")
    setWithLines(false)
  }, [isOpen, defaultEmail])

  const handleSubmit = () => {
    if (submitting) return
    setSubmitting(true)

    const body = {
      ids,
      export_mode: mode,
      with_lines:  withLines,
      recipient:   mode === MODE_SEND ? recipient : undefined,
    }

    postRequest({ url: csvUrl, body })
      .then(response => {
        if (!response || response.success === false) {
          flashAlert(I18n.t("mbc_contracts.csv.export_failed"))
          return
        }
        if (response.send_by_email) {
          flashNotice(I18n.t("mbc_contracts.csv.email_pending"))
        } else if (response.visualize_url) {
          window.open(response.visualize_url, "_blank")
        }
        onClose()
      })
      .catch(() => flashAlert(I18n.t("quotes.builder.footer.network_error")))
      .finally(() => setSubmitting(false))
  }

  const footer = (
    <>
      <div />
      <div className="flex items-center gap-2">
        <Button variant="ghost" onClick={onClose}>
          {I18n.t("button.cancel")}
        </Button>
        <Button
          variant  = "primary"
          onClick  = {handleSubmit}
          disabled = {submitting || (mode === MODE_SEND && !recipient)}
        >
          <FontAwesomeIcon icon={submitting ? "spinner" : "file-export"} spin={submitting} />
          {I18n.t("mbc_contracts.csv.run_export")}
        </Button>
      </div>
    </>
  )

  return (
    <Modal
      isOpen  = {isOpen}
      onClose = {onClose}
      title   = {I18n.t("mbc_contracts.csv.title")}
      footer  = {footer}
      size    = "sm"
    >
      <p className="text-sm text-slate-600">
        <span className="font-semibold text-slate-900 tabular-nums">{count}</span>{" "}
        {I18n.t("mbc_contracts.csv.count_to_export", { count })}
      </p>

      <div className="space-y-2 mt-4">
        <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <input
            type      = "radio"
            name      = "scope"
            value     = "without_lines"
            checked   = {!withLines}
            onChange  = {() => setWithLines(false)}
            className = "mt-1"
          />
          <div>
            <div className="text-sm font-medium text-slate-900">
              {I18n.t("mbc_contracts.csv.without_lines")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {I18n.t("mbc_contracts.csv.without_lines_description")}
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <input
            type      = "radio"
            name      = "scope"
            value     = "with_lines"
            checked   = {withLines}
            onChange  = {() => setWithLines(true)}
            className = "mt-1"
          />
          <div>
            <div className="text-sm font-medium text-slate-900">
              {I18n.t("mbc_contracts.csv.with_lines")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {I18n.t("mbc_contracts.csv.with_lines_description")}
            </div>
          </div>
        </label>
      </div>

      <div className="space-y-2 mt-4">
        <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <input
            type      = "radio"
            name      = "export_mode"
            value     = {MODE_VISUALIZE}
            checked   = {mode === MODE_VISUALIZE}
            onChange  = {() => setMode(MODE_VISUALIZE)}
            className = "mt-1"
          />
          <div>
            <div className="text-sm font-medium text-slate-900">
              {I18n.t("mbc_contracts.csv.visualize_label")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {I18n.t("mbc_contracts.csv.visualize_description")}
            </div>
          </div>
        </label>

        <label className="flex items-start gap-3 p-3 rounded-md border border-slate-200 hover:bg-slate-50 cursor-pointer">
          <input
            type      = "radio"
            name      = "export_mode"
            value     = {MODE_SEND}
            checked   = {mode === MODE_SEND}
            onChange  = {() => setMode(MODE_SEND)}
            className = "mt-1"
          />
          <div className="flex-1">
            <div className="text-sm font-medium text-slate-900">
              {I18n.t("mbc_contracts.csv.email_label")}
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {I18n.t("mbc_contracts.csv.email_description")}
            </div>
            {mode === MODE_SEND && (
              <input
                type         = "email"
                value        = {recipient}
                onChange     = {event => setRecipient(event.target.value)}
                placeholder  = {I18n.t("mbc_contracts.csv.email_placeholder")}
                required
                autoComplete = "off"
                className    = "mt-2 w-full h-9 px-3 rounded-md border border-slate-200 text-sm focus:border-brand-500"
              />
            )}
          </div>
        </label>
      </div>
    </Modal>
  )
}

export default CsvExportModal
